import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useQuery } from "@tanstack/react-query";
import { getDsApp } from "../../services/pdmApi.service";
import { repositoryUrlIsWebLink } from "../../utils/repositoryRemote";

function formatDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function DsAppDetailsDialog(props: {
  open: boolean;
  dsAppId: string | null;
  onClose: () => void;
}) {
  const { open, dsAppId, onClose } = props;

  const detailsQuery = useQuery({
    queryKey: ["dsApp", dsAppId],
    queryFn: () => getDsApp(dsAppId as string),
    enabled: open && Boolean(dsAppId),
  });

  const dsApp = detailsQuery.data ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>DSApp details</DialogTitle>
      <DialogContent dividers>
        {detailsQuery.isPending && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {detailsQuery.isError && (
          <Alert severity="error">
            {detailsQuery.error instanceof Error
              ? detailsQuery.error.message
              : "Failed to load DSApp."}
          </Alert>
        )}
        {detailsQuery.isSuccess && dsApp && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <DetailRow label="Name" value={dsApp.name} />
            <DetailRow label="Version" value={dsApp.version} />
            <DetailRow label="Description" value={dsApp.description} />
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Repository URL
              </Typography>
              {repositoryUrlIsWebLink(dsApp.repositoryUrl) ? (
                <Link
                  href={dsApp.repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {dsApp.repositoryUrl}
                </Link>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {dsApp.repositoryUrl}
                </Typography>
              )}
            </Stack>
            <DetailRow label="Git branch" value={dsApp.gitBranch} />
            <Divider />
            <DetailRow label="ID" value={dsApp.id} monospace />
            <DetailRow label="Created" value={formatDate(dsApp.createdAt)} />
            <DetailRow label="Updated" value={formatDate(dsApp.updateAt)} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow(props: {
  label: string;
  value: string;
  monospace?: boolean;
}) {
  const { label, value, monospace } = props;
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: monospace ? "monospace" : undefined }}>
        {value}
      </Typography>
    </Stack>
  );
}
